import { useMemo } from "react"
import {
  IndexableItem,
  NormalizedSearchOptions,
  SearchResultItem,
  useNormalizedSearch
} from "../../utils/search-normalize"
import { getIndexableBlock } from "./blocks"
import {
  NavigatorSearchResult,
  SearchableBlockType,
  SearchablePathItem
} from "./types"

export const getIsPathItem = (
  item:
    | SearchablePathItem
    | SearchableBlockType
    | IndexableItem
    | SearchResultItem
): item is SearchablePathItem => {
  return (
    item.type === "step" || item.type === "lesson" || item.type === "resource"
  )
}

const stepToIndexable = (step: SearchablePathItem): IndexableItem => {
  return {
    id: step.id,
    type: step.type,
    title: step.name,
    description: step.description as string | undefined
  }
}

const lessonToIndexable = (lesson: SearchablePathItem): IndexableItem => {
  return {
    id: lesson.id,
    type: lesson.type,
    title: lesson.name,
    description: lesson.description as string | undefined
  }
}

const blockToIndexable = (block: SearchableBlockType): IndexableItem | null => {
  const indexableBlock = getIndexableBlock(block)

  return indexableBlock
    ? ({
        id: block.id,
        type: block.type,
        parentPath: block.contextKey,
        ...getIndexableBlock(block)
      } as IndexableItem)
    : null
}

const getIndexableItems = (
  items: (SearchablePathItem | SearchableBlockType)[]
) => {
  return items.reduce((acc, item) => {
    if (item.type === "step") {
      acc.push(stepToIndexable(item as SearchablePathItem))
    } else if (item.type === "lesson") {
      acc.push(lessonToIndexable(item as SearchablePathItem))
    } else {
      const indexableBlock = blockToIndexable(item as SearchableBlockType)
      if (indexableBlock) {
        acc.push(indexableBlock)
      }
    }
    return acc
  }, [] as IndexableItem[])
}

// Given a set of lessons, steps and blocks
// Nest and sort them in Path order
const sortItems = (
  lessons: SearchablePathItem[],
  steps: SearchablePathItem[],
  blocks: SearchableBlockType[]
) => {
  // 3. Sort and collate items: lessons, steps, blocks
  const renderedItems: (SearchablePathItem | SearchableBlockType)[] = []

  // sort lessons
  lessons.sort((a, b) => {
    return (a.order ?? 0) - (b.order ?? 0)
  })

  // collate steps inside lessons
  lessons.forEach((lesson) => {
    renderedItems.push(lesson)
    const lessonSteps = steps.filter((step) => step.parent_id === lesson.id)
    lessonSteps.sort((a, b) => {
      return (a.order ?? 0) - (b.order ?? 0)
    })
    lessonSteps.forEach((step) => {
      // get blocks for step
      let stepBlocks = blocks.filter(
        (block) => block.contextKey === `/${String(step.source_id)}`
      )
      // dedupe by id in O(n)
      stepBlocks = Array.from(
        new Map(stepBlocks.map((b) => [b.id, b])).values()
      )
      stepBlocks.sort((a, b) => {
        return a.order - b.order
      })
      renderedItems.push(step, ...stepBlocks)
    })
  })

  return renderedItems
}

// Return all original items in their original ordering
// Include match metadata for all items that are matched
// Nest blocks inside steps; nest steps inside lessons
// Include all matched Blocks
// Include Steps when they're matched or child Blocks are matched
// Include Lessons when they're match or child Steps are matched
const selectAndSortItems = (
  allItems: SearchablePathItem[],
  allBlocks: SearchableBlockType[],
  searchResults: SearchResultItem[]
): (SearchablePathItem | SearchableBlockType)[] => {
  // If no search results, sort and return all items
  if (!searchResults?.length) {
    const lessons = allItems.filter((item) => item.type === "lesson")
    const steps = allItems.filter(
      (item) => item.type === "step" || item.type === "resource"
    )
    return sortItems(lessons, steps, allBlocks)
  }

  const pathItemTypes = ["step", "resource", "lesson"]

  // Make lookups for search results

  // make lookup for all Path items found in search
  const resultItemsLookup = new Map<string | number, SearchablePathItem>()
  searchResults.forEach((result) => {
    const isPathItem = getIsPathItem(result)

    if (!isPathItem) return

    const item = allItems.find(
      (item) => item.id === result.id
    ) as SearchablePathItem
    if (item) {
      resultItemsLookup.set(item.id, {
        ...item,
        searchResult: result as NavigatorSearchResult
      })
    }
  })

  // make lookup for all blocks found in search
  const resultBlocksLookup = new Map<string, SearchableBlockType>()
  searchResults.forEach((result) => {
    // We assume that an item is a Block if it's not a Path Item
    const isBlock = !pathItemTypes.includes(result.type)

    if (!isBlock) return

    const block = allBlocks.find((block) => block.id === result.id)
    if (block) {
      resultBlocksLookup.set(block.id, {
        ...block,
        searchResult: result as NavigatorSearchResult
      })
    }
  })

  // Collect all items: those that are matched and those that are included because their children are matched
  // get blocks matched by search term
  const matchedBlocks = allBlocks
    .filter((block) => resultBlocksLookup.has(String(block.id)))
    .map((block) => {
      const searchBlock = resultBlocksLookup.get(String(block.id))
      if (!searchBlock || !searchBlock.searchResult) return block

      return searchBlock
    })

  // get step matched by search term
  const matchedSteps = allItems
    .filter(
      (item) =>
        (item.type === "step" || item.type === "resource") &&
        resultItemsLookup.has(item.id)
    )
    .map((item) => {
      const searchStep = resultItemsLookup.get(item.id)
      if (!searchStep || !searchStep.searchResult) return item

      return searchStep
    })

  // get steps that have matched blocks
  const blocksSteps = allItems.filter(
    (item) =>
      item.type === "step" &&
      matchedBlocks.some(
        (block) => block.contextKey === `/${String(item.source_id)}`
      )
  )

  // merge and dedupe steps
  const steps = [
    ...new Map([...blocksSteps, ...matchedSteps].map((s) => [s.id, s])).values()
  ]

  // get lessons
  const matchedLessons = allItems
    .filter((item) => item.type === "lesson" && resultItemsLookup.has(item.id))
    .map((item) => {
      const searchLesson = resultItemsLookup.get(item.id)
      if (!searchLesson || !searchLesson.searchResult) return item
      return searchLesson
    })

  // get lessons that have matched steps
  const stepsLessons = allItems.filter(
    (item) =>
      item.type === "lesson" && steps.some((step) => step.parent_id === item.id)
  )
  // merge and dedupe lessons
  const lessons = [
    ...new Map(
      [...stepsLessons, ...matchedLessons].map((l) => [l.id, l])
    ).values()
  ]

  return sortItems(lessons, steps, matchedBlocks)
}

// Takes a flat list of steps, lessons and all blocks for all steps in the current Path
// Each Block should have a contextKey to identify the step it belongs to
const useNavigatorSearch = (
  pathItems: SearchablePathItem[] = [],
  blocks: SearchableBlockType[] = [],
  query: string = ""
) => {
  // Convert all items (Steps, Lessons, Blocks) into IndexableItems
  // This allows them all to be indexed with a similar shape: id, title, description, content, parentPath
  // Re-index when pathItems or blocks change, ensuring we have latest content (affects search results),
  // prefering this over checking length of arrays for that reason. Since we're skipping both related queries
  // when the Path Navigator is closed, this should not be a performance concern. Otherwise, we'd reindex on every
  // Path item or Block content change (which would not be good).
  // This is assumes the following lifecycle:
  // 1. on initial load, 0 Path items and 0 Blocks exist
  // 2. when Path Items are loaded, index is updated
  // 3. when Blocks are loaded, index is updated
  // 4. when Blocks or Path Items are updated, added or deleted, the Navigator has been closed;
  //   when re-opened, it is remounted, thus triggering a re-indexing of all items
  const indexableItems = useMemo(() => {
    const items = [
      ...getIndexableItems(pathItems),
      ...getIndexableItems(blocks)
    ]
    const dedupedItems = items.filter(
      (item, index, self) => index === self.findIndex((i) => i.id === item.id)
    )
    return dedupedItems
  }, [pathItems, blocks])

  const options = useMemo(
    () => ({
      indexOptions: {},
      searchOptions: {
        boost: { title: 2, description: 1, content: 0.5 }, // prioritize matching based on result fields
        prefix: true, // allow partial word matching, if search is at start of word
        fuzzy: 0.2, // allow light typos (reduce for more strict matching)
        combineWith: "AND" // combine tokens/terms with AND (OR would allow matching on any term provided in search)
      }
    }),
    []
  ) as NormalizedSearchOptions

  return useNormalizedSearch(indexableItems, query, options)
}

// Debounced Results Hook
export const useNavigatorSearchResults = (
  pathItems: SearchablePathItem[] = [],
  blocks: SearchableBlockType[] = [],
  query: string = ""
): (SearchablePathItem | SearchableBlockType)[] => {
  const searchResults = useNavigatorSearch(pathItems, blocks, query)

  const renderedItems = useMemo(() => {
    if (!searchResults?.length && !!query) return []
    return selectAndSortItems(pathItems, blocks, searchResults)
  }, [searchResults, query])

  return renderedItems
}
